import { mapValues } from "lodash";
import React, { useContext } from "react";

import {
  getAllTagValuesFromTags,
  getFilteredNestedTermAggregationBuckets,
  getFilteredTermAggregationBuckets,
  replaceByMatch,
} from "@examspark/common";
import {
  bloomsMeta,
  dokMeta,
  languageMeta,
} from "@examspark/common/dist/meta/assets";
import {
  INTERACTION_MAP,
  hasDerivedInteractionTypes,
} from "@examspark/common/dist/meta/questions";
import { SearchContext } from "@examspark/common-app-search";
import { gradeLevelList, gradeLevelMeta } from "@examspark/common-meta";
import { Button, KeyboardAccessible, VSpace } from "@examspark/common-ui";
import { BucketAggregation, DeliveryMediaType } from "@examspark/types";

import FacetCheckboxFilter from "../../../filters/FacetCheckboxFilter";
import FilterBank from "../../../filters/FilterBank";
import FilterObjective from "../../../filters/FilterObjective";
import FilterObjectiveScope from "../../../filters/FilterObjectiveScope";
import FilterUnits from "../../../filters/FilterUnit";
import { ActivityEditorConfigContext } from "../ActivityEditor";

export const ItemPickerFilters = () => {
  const config = useContext(ActivityEditorConfigContext);
  const search = useContext(SearchContext);

  return (
    <VSpace size="2rem">
      <KeyboardAccessible>
        <Button
          stretch
          title="Skip to Results"
          variant="outlined"
          onClick={() => {
            // this could be cleaner
            const results = document.getElementById("item-picker-filters");
            results?.focus();
          }}
        >
          Skip to Results
        </Button>
      </KeyboardAccessible>
      {config?.itemPicker?.filters?.bank?.show && (
        <FilterBank
          propertyName="Bank"
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "bankId",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          selected={search?.state.filter.bankId ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                bankId: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.subject?.show && (
        <FacetCheckboxFilter
          propertyName="Subject"
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "subject",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          labelOverrides={{
            ELA: "English Language Arts",
            SLA: "Spanish Language Arts",
            MATH: "Mathematics",
            SCI: "Science",
            SS: "Social Studies",
          }}
          selected={search?.state.filter.subject ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                subject: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.unit?.show && (
        <FilterUnits
          propertyName={
            config.itemPicker.filters.unit?.title ?? "Course & Unit/Module"
          }
          isLoading={search?.facets.isLoading}
          options={
            getFilteredNestedTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "referencesIdsUnit",
            ).map((agg: BucketAggregation) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          selected={search?.state.filter.referencesIdsUnit ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                referencesIdsUnit: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.grade?.show && (
        <FacetCheckboxFilter
          propertyName="Grade"
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "grade",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          labelOverrides={gradeLevelList.reduce(
            (acc, grade) => {
              acc[grade] = gradeLevelMeta[grade].label;
              return acc;
            },
            {} as Record<string, string>,
          )}
          selected={search?.state.filter.grade ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                grade: values,
              },
            });
          }}
        />
      )}
      {/* REMINDER: this won't appear unless we have a subject selected */}
      {config?.itemPicker?.filters?.objective?.show && (
        <FilterObjectiveScope
          propertyName={
            config?.itemPicker?.filters?.objective?.title ??
            "Standard Subject/Grade"
          }
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "objectiveScope",
            )
              .map((agg) => {
                return {
                  label: agg.key,
                  value: agg.key,
                  count: String(agg.doc_count),
                };
              })
              .filter(
                // The Objective filter requires at least one designated filter (default "subject")
                // to be selected before it will show any options
                // unless there are already selected options, in which case show those
                // specifically so the user can remove them
                (f) => {
                  const precondition = config?.itemPicker?.filters?.objective
                    ?.requirePrecondition ?? ["subject"];

                  const preconditionMet = precondition.every((p) => {
                    const field = search?.state.filter[p];
                    if (Array.isArray(field)) {
                      return field.length > 0;
                    }
                    return field !== undefined;
                  });

                  return (
                    preconditionMet ||
                    search?.state.filter.objectiveScope?.includes(f.value)
                  );
                },
              ) ?? []
          }
          selected={search?.state.filter.objectiveScope ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                objectiveScope: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.objective?.show && (
        <FilterObjective
          propertyName="Standard"
          isLoading={search?.facets.isLoading}
          options={getFilteredTermAggregationBuckets(
            search?.facets.data?.aggregations as any,
            "alignmentIds",
          )
            .map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            })
            .filter(
              (f) =>
                // If we don't have a scope to narrow down to, don't show anything
                // except already-selected standards
                search?.state.filter.objectiveScope?.length ||
                search?.state.filter.alignmentIds?.includes(f.value),
            )}
          selected={search?.state.filter.alignmentIds ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                alignmentIds: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.dok?.show && (
        <FacetCheckboxFilter
          propertyName="Depth of Knowledge"
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "dok",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          labelOverrides={mapValues(dokMeta, (m) => m.label)}
          selected={search?.state.filter.dok ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                dok: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.blooms?.show && (
        <FacetCheckboxFilter
          propertyName="Bloom's Taxonomy"
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "blooms",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          labelOverrides={mapValues(bloomsMeta, (m) => m.label)}
          selected={search?.state.filter.blooms ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                blooms: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.interaction?.show && (
        <FacetCheckboxFilter
          propertyName="Question Type"
          isLoading={search?.facets.isLoading}
          options={getFilteredTermAggregationBuckets(
            search?.facets.data?.aggregations as any,
            "interaction",
          )
            .map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            })
            .filter((term) => !hasDerivedInteractionTypes(term.value))}
          labelOverrides={mapValues(
            INTERACTION_MAP,
            (m) => m.longLabel ?? m.label,
          )}
          selected={search?.state.filter.interaction ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                interaction: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.customTags?.map((customTag, i) => (
        <FacetCheckboxFilter
          key={i}
          propertyName={customTag.label ?? customTag.key}
          isLoading={search?.facets.isLoading}
          options={getFilteredNestedTermAggregationBuckets(
            search?.facets.data?.aggregations ?? {},
            `tag:${customTag.key}`,
          ).map((agg) => ({
            label: agg.key,
            value: agg.key,
            count: String(agg.doc_count),
          }))}
          selected={getAllTagValuesFromTags(
            search?.state.filter.tag,
            customTag.key,
          )}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                tag: replaceByMatch(
                  search?.state.filter.tag,
                  (t) => t.key === customTag.key,
                  values.map((value) => ({ key: customTag.key, value })),
                ),
              },
            });
          }}
        />
      ))}
      {config?.itemPicker?.filters?.deliveryMediaType?.show && (
        <FacetCheckboxFilter
          propertyName="Delivery Method"
          isLoading={search?.facets.isLoading}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "deliveryMediaType",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          labelOverrides={{
            [DeliveryMediaType.DigitalOnly]: "Digital Only",
            [DeliveryMediaType.PrintOnly]: "Print Only",
            [DeliveryMediaType.PrintAndDigital]: "Print and Digital",
          }}
          selected={search?.state.filter.deliveryMediaType ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                deliveryMediaType: values,
              },
            });
          }}
        />
      )}
      {config?.itemPicker?.filters?.language?.show && (
        <FacetCheckboxFilter
          propertyName="Language"
          isLoading={search?.facets.isLoading}
          labelOverrides={{
            EN: languageMeta["EN"].label,
            ES: languageMeta["ES"].label,
          }}
          options={
            getFilteredTermAggregationBuckets(
              search?.facets.data?.aggregations as any,
              "language",
            ).map((agg) => {
              return {
                label: agg.key,
                value: agg.key,
                count: String(agg.doc_count),
              };
            }) ?? []
          }
          selected={search?.state.filter.language ?? []}
          onFilterChange={(values) => {
            search?.dispatch({
              type: "UPDATE",
              data: {
                language: values,
              },
            });
          }}
        />
      )}
    </VSpace>
  );
};

export default ItemPickerFilters;
